import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Loader2, Check, CreditCard, X } from 'lucide-react';
import { withTenant } from './BookingPage';

/*
 * "Pay now" — the page the link in an invoice email opens (0077, reworked in 0078).
 *
 * ⚠️ ON THE BUSINESS'S OWN DOMAIN, AND IT LOOKS LIKE HER. The customer is paying their groomer,
 * not a platform they've never heard of. Her name first, her accent color, and StartMeUpPro
 * nowhere. Stripe's card page follows, under her Stripe account's name.
 *
 * ⚠️ OPENING THIS PAGE CREATES NOTHING, and neither does following its button: it opens HER
 * Stripe Payment Link with the amount and invoice filled in (0078). Our Stripe account isn't in it.
 *
 * ⚠️ TWO WAYS IN, ONE PAGE (0092). From the invoice email or text, on the customer's own phone:
 * the buttons open Stripe, Cash App or Venmo as they always have. From "Show to customer" in her
 * CRM (`?here=1`), on HER phone, handed across: every button shows a QR code instead, for the
 * customer to scan and pay on their own phone. Tapping Cash App on her phone would open HER
 * Cash App and have her paying herself.
 */

interface PayPageData {
  businessName: string;
  number: string;
  amountCents: number;
  /** The tip chosen so far, which every link below already includes (0092). */
  tipCents: number;
  paid: boolean;
  paidOn: string | null;
  /** Her own Stripe Payment Link, filled in for this invoice (0078). Null when paid or not set up. */
  payUrl: string | null;
  /** Her other way to pay, worded as the invoice email words it. */
  otherWay: string[] | null;
  /** Her other ways as buttons (Venmo, Cash App, …), Zelle as lines, then the payment-note line. */
  otherPay?: { buttons: PayButton[]; lines: string[]; note: string } | null;
  accentColor: string | null;
}

interface PayButton {
  method: string;
  label: string;
  href: string;
  shown: string;
  bg: string;
  fg: string;
}

const FALLBACK_ACCENT = '#2F6B4F';
const money = (c: number) => `$${(c / 100).toFixed(2)}`;
/** The tip buttons. Percent of the invoice, rounded to the cent. */
const TIP_PERCENTS = [15, 20, 25] as const;

/** `/pay/<token>`, parsed without a router — same as `/review`. */
export function parsePayPath(pathname: string): { token: string } | null {
  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  if (parts[0] !== 'pay' || !parts[1] || parts.length > 2) return null;
  return { token: parts[1] };
}

export default function PayPage({ token }: { token: string }) {
  const [data, setData] = useState<PayPageData | null>(null);
  const [failure, setFailure] = useState<'missing' | 'error' | null>(null);
  const here = new URLSearchParams(window.location.search).get('here') === '1';
  /** The button whose QR code is showing, on her phone (`here`). */
  const [qr, setQr] = useState<{ label: string; href: string; note: string | null } | null>(null);
  const [tipBusy, setTipBusy] = useState(false);
  const [tipFailed, setTipFailed] = useState(false);
  const [custom, setCustom] = useState<string | null>(null);

  /**
   * Save the tip, and take the page back re-filled — every link's amount comes from the server
   * (routes/public-pay.ts), so nothing here does its own arithmetic on what gets charged.
   */
  const chooseTip = async (tipCents: number) => {
    if (!data || tipBusy || tipCents === data.tipCents) return;
    setTipBusy(true);
    setTipFailed(false);
    try {
      const r = await fetch(withTenant(`/api/public/pay/${encodeURIComponent(token)}/tip`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipCents }),
      });
      if (!r.ok) throw new Error(String(r.status));
      setData((await r.json()) as PayPageData);
    } catch {
      setTipFailed(true);
    } finally {
      setTipBusy(false);
    }
  };

  useEffect(() => {
    let live = true;
    fetch(withTenant(`/api/public/pay/${encodeURIComponent(token)}`))
      .then(async (r) => {
        if (!live) return;
        if (r.status === 404) return setFailure('missing');
        if (!r.ok) return setFailure('error');
        setData((await r.json()) as PayPageData);
      })
      .catch(() => live && setFailure('error'));
    return () => {
      live = false;
    };
  }, [token]);

  const accent = data?.accentColor ?? FALLBACK_ACCENT;

  /*
   * ⚠️ STRAIGHT ON TO STRIPE WHEN THEY TAPPED "PAY BY CARD" (`?card=1`, Mike 2026-10-01). The tap
   * in the email was the choice; a second button here only cost a tap. Only once we know the
   * invoice is unpaid and her link exists: a paid invoice still says "Paid", which is why the
   * email goes through this page rather than linking to Stripe directly. `replace`, so Back from
   * Stripe returns to the email rather than bouncing here and forward again.
   */
  const goingToCard = Boolean(
    data && !data.paid && data.payUrl && new URLSearchParams(window.location.search).get('card') === '1',
  );
  useEffect(() => {
    if (goingToCard && data?.payUrl) window.location.replace(data.payUrl);
  }, [goingToCard, data?.payUrl]);

  if (failure === 'missing') {
    return (
      <Shell>
        <p className="text-slate-700">This payment link isn’t valid.</p>
        <p className="mt-2 text-sm text-slate-500">Reply to the invoice email and the business will help you pay.</p>
      </Shell>
    );
  }
  if (failure === 'error') {
    return (
      <Shell>
        <p className="text-slate-700">We couldn’t load this just now.</p>
        <p className="mt-2 text-sm text-slate-500">It’s us, not you. Try again in a moment.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-5 inline-flex items-center rounded-lg bg-slate-800 px-5 py-2.5 font-semibold text-white transition-colors hover:bg-slate-700"
        >
          Try again
        </button>
      </Shell>
    );
  }
  if (!data || goingToCard) {
    return (
      <Shell>
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" />
        {goingToCard && <p className="mt-3 text-sm text-slate-500">Opening secure checkout…</p>}
      </Shell>
    );
  }

  return (
    <Shell>
      <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: accent }}>
        {data.businessName}
      </p>
      <h1 className="mt-2 text-2xl font-bold text-slate-900">Invoice {data.number}</h1>
      <p className="mt-1 text-4xl font-bold text-slate-900">{money(data.amountCents)}</p>

      {!data.paid && (
        <TipPicker
          amountCents={data.amountCents}
          tipCents={data.tipCents}
          accent={accent}
          busy={tipBusy}
          failed={tipFailed}
          custom={custom}
          setCustom={setCustom}
          onChoose={(c) => void chooseTip(c)}
        />
      )}
      {data.paid && data.tipCents > 0 && (
        <p className="mt-1 text-sm text-slate-500">plus {money(data.tipCents)} tip</p>
      )}

      {data.paid ? (
        <div className="mt-8">
          <Badge accent={accent} />
          <p className="mt-3 font-semibold text-slate-800">Paid — thank you!</p>
          {data.paidOn && <p className="mt-1 text-sm text-slate-500">Paid on {data.paidOn}.</p>}
        </div>
      ) : (
        <div className="mt-8">
          {/*
            ⚠️ A PLAIN LINK TO HER OWN STRIPE, NOT A BUTTON THAT CALLS US. The amount is filled in
            on Stripe's page and the customer CAN change it there; the webhook settles the invoice
            only when the amount matches (routes/stripe-pay.ts).
          */}
          {here && (data.payUrl || data.otherPay) && (
            <p className="mb-4 text-sm font-medium text-slate-700">Tap how you’d like to pay, then scan the code with your phone.</p>
          )}
          {data.payUrl && (
            <a
              href={data.payUrl}
              onClick={(e) => {
                if (!here) return;
                e.preventDefault();
                setQr({ label: 'Pay by card', href: data.payUrl!, note: null });
              }}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-lg font-semibold text-white shadow-sm"
              style={{ backgroundColor: accent }}
            >
              <CreditCard className="h-5 w-5" />
              Pay {money(data.amountCents + data.tipCents)} by card
            </a>
          )}
          {data.payUrl && <p className="mt-2 text-xs text-slate-500">On Stripe’s secure payment page.</p>}
          {data.otherPay ? (
            <div className={data.payUrl ? 'mt-8 border-t border-slate-200 pt-6' : ''}>
              {data.payUrl && <p className="mb-3 text-sm font-semibold text-slate-800">Or pay another way</p>}
              {/* Each in its service's own colors, her handle under it so the customer sees who they're paying. */}
              {data.otherPay.buttons.map((b) => (
                <div key={b.method} className="mb-4">
                  <a
                    href={b.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (!here) return;
                      e.preventDefault();
                      // Venmo's link carries the invoice number as the note; Cash App's can't.
                      setQr({ label: b.label, href: b.href, note: b.method === 'venmo' ? null : data.otherPay!.note });
                    }}
                    className="inline-flex w-full items-center justify-center rounded-xl px-5 py-3 text-base font-semibold shadow-sm"
                    style={{ backgroundColor: b.bg, color: b.fg }}
                  >
                    {b.label}
                  </a>
                  <p className="mt-1 text-xs text-slate-500">{b.shown}</p>
                </div>
              ))}
              {data.otherPay.lines.map((line) => (
                <p key={line} className="mt-1 break-words text-sm text-slate-700">
                  <Linkified text={line} />
                </p>
              ))}
              <p className="mt-2 text-sm text-slate-600">{data.otherPay.note}</p>
            </div>
          ) : (
            data.otherWay && (
              <div className={`${data.payUrl ? 'mt-8 border-t border-slate-200 pt-6' : ''} text-left text-sm text-slate-700`}>
                {data.payUrl && <p className="mb-2 font-semibold text-slate-800">Or pay another way</p>}
                {data.otherWay.map((line) => (
                  <p key={line} className="mt-1 break-words">
                    <Linkified text={line} />
                  </p>
                ))}
              </div>
            )
          )}
          {!data.payUrl && !data.otherWay && (
            <p className="text-sm text-slate-600">Reply to the invoice email to arrange payment.</p>
          )}
        </div>
      )}
      {qr && <QrSheet qr={qr} totalCents={data.amountCents + data.tipCents} onClose={() => setQr(null)} />}
    </Shell>
  );
}

/**
 * No tip / 15% / 20% / 25% / Custom, and the new total under it.
 *
 * ⚠️ "NO TIP" IS A BUTTON OF ITS OWN, AND IT'S WHERE THE PAGE STARTS. A customer handed someone
 * else's phone shouldn't have to work out how to say no, and a pre-picked 20% is a tip they
 * didn't choose.
 */
function TipPicker({
  amountCents,
  tipCents,
  accent,
  busy,
  failed,
  custom,
  setCustom,
  onChoose,
}: {
  amountCents: number;
  tipCents: number;
  accent: string;
  busy: boolean;
  failed: boolean;
  custom: string | null;
  setCustom: (v: string | null) => void;
  onChoose: (tipCents: number) => void;
}) {
  if (!(amountCents > 0)) return null;
  const options = TIP_PERCENTS.map((p) => ({ label: `${p}%`, cents: Math.round((amountCents * p) / 100) }));
  const isPreset = tipCents === 0 || options.some((o) => o.cents === tipCents);
  const customOpen = custom !== null || !isPreset;
  const parsedCustom = (() => {
    const v = (custom ?? '').trim().replace(/^\$/, '');
    if (!/^\d+(\.\d{0,2})?$/.test(v)) return null;
    const c = Math.round(Number(v) * 100);
    return c <= amountCents ? c : null;
  })();
  const chip = (selected: boolean) =>
    `min-w-0 rounded-xl border px-0.5 py-2.5 text-[13px] font-semibold leading-tight transition-colors disabled:opacity-60 ${
      selected ? 'text-white' : 'border-slate-200 bg-white text-slate-800'
    }`;
  const chipStyle = (selected: boolean) => (selected ? { backgroundColor: accent, borderColor: accent } : undefined);

  return (
    <div className="mt-6 text-left">
      <p className="text-sm font-semibold text-slate-800">Add a tip?</p>
      <div className="mt-2 grid grid-cols-5 gap-1.5">
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setCustom(null);
            onChoose(0);
          }}
          className={chip(tipCents === 0 && !customOpen)}
          style={chipStyle(tipCents === 0 && !customOpen)}
        >
          No tip
        </button>
        {options.map((o) => {
          const selected = tipCents > 0 && tipCents === o.cents && custom === null;
          return (
            <button
              key={o.label}
              type="button"
              disabled={busy}
              onClick={() => {
                setCustom(null);
                onChoose(o.cents);
              }}
              className={chip(selected)}
              style={chipStyle(selected)}
            >
              {o.label}
              <span className={`block text-xs font-normal ${selected ? 'text-white/90' : 'text-slate-500'}`}>{money(o.cents)}</span>
            </button>
          );
        })}
        <button
          type="button"
          disabled={busy}
          onClick={() => setCustom(custom ?? (tipCents > 0 ? (tipCents / 100).toFixed(2) : ''))}
          className={chip(customOpen)}
          style={chipStyle(customOpen)}
        >
          Custom
        </button>
      </div>
      {customOpen && (
        <form
          className="mt-3 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (parsedCustom !== null) onChoose(parsedCustom);
          }}
        >
          <span className="text-slate-700">$</span>
          <input
            value={custom ?? ''}
            onChange={(e) => setCustom(e.target.value)}
            inputMode="decimal"
            aria-label="Tip amount"
            placeholder="0.00"
            className="w-28 rounded-lg border border-slate-300 px-3 py-2 text-base outline-none focus:border-slate-500"
          />
          <button
            type="submit"
            disabled={busy || parsedCustom === null}
            className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            Add tip
          </button>
        </form>
      )}
      {customOpen && custom && parsedCustom === null && (
        <p className="mt-1 text-xs text-slate-500">Up to {money(amountCents)}.</p>
      )}
      {failed && <p className="mt-2 text-sm text-red-600">That didn’t save. Try again.</p>}
      <p className="mt-4 flex items-baseline justify-between border-t border-slate-200 pt-3 text-slate-900">
        <span className="font-semibold">Total</span>
        <span className="text-2xl font-bold">
          {busy ? <Loader2 className="inline h-5 w-5 animate-spin text-slate-400" /> : money(amountCents + tipCents)}
        </span>
      </p>
    </div>
  );
}

/**
 * The QR code for one way to pay, on her phone, for the customer to scan with theirs.
 *
 * ⚠️ DRAWN HERE, FROM THE LINK ALREADY ON THE PAGE. The link never goes to a QR service; it's
 * the same one the button would have opened, so the amount, the tip and (for Venmo) the invoice
 * number in the note all come with it.
 */
function QrSheet({
  qr,
  totalCents,
  onClose,
}: {
  qr: { label: string; href: string; note: string | null };
  totalCents: number;
  onClose: () => void;
}) {
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void QRCode.toString(qr.href, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' }).then((s) => {
      if (live) setSvg(s);
    });
    return () => {
      live = false;
    };
  }, [qr.href]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4" role="dialog" aria-modal="true" aria-label={qr.label}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
        <div className="flex justify-end">
          <button type="button" onClick={onClose} aria-label="Close" className="-m-2 rounded-lg p-2 text-slate-500">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-lg font-bold text-slate-900">{qr.label}</p>
        <p className="mt-1 text-3xl font-bold text-slate-900">{money(totalCents)}</p>
        <div className="mx-auto mt-4 w-64 max-w-full">
          {svg ? (
            <div className="[&>svg]:h-auto [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: svg }} />
          ) : (
            <div className="grid aspect-square place-items-center">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          )}
        </div>
        <p className="mt-4 font-semibold text-slate-800">Point your phone’s camera at the code.</p>
        <p className="mt-1 text-sm text-slate-600">It opens on your phone with the amount filled in.</p>
        {qr.note && <p className="mt-2 text-sm text-slate-600">{qr.note}</p>}
        <button
          type="button"
          onClick={onClose}
          className="mt-6 w-full rounded-xl border border-slate-300 px-5 py-3 font-semibold text-slate-800"
        >
          Done
        </button>
      </div>
    </div>
  );
}

/** "Pay by Venmo: https://…" with the link tappable. Only https links, which is all we store. */
function Linkified({ text }: { text: string }) {
  const parts = text.split(/(https:\/\/\S+)/);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('https://') ? (
          <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-2">
            {p}
          </a>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white px-6 py-14">
      <div className="mx-auto max-w-md text-center">{children}</div>
    </div>
  );
}

function Badge({ accent }: { accent: string }) {
  return (
    <div className="mx-auto grid h-12 w-12 place-items-center rounded-full" style={{ backgroundColor: `${accent}1a` }}>
      <Check className="h-6 w-6" style={{ color: accent }} />
    </div>
  );
}
